import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Section, formatDateTime } from '@/pages/settings/datacenter/shared'
import { MessageModal, type MessageModalData } from '@/components/ui/MessageModal'
import { portalApi } from './portal-api'

/** Müşteri portalı: lisans başına Yapay Zekâ kredi bakiyesi, bu ayki tüketim ve kredi hareketleri */

interface Usage { calls: number; inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheWriteTokens: number; credits: string; usd: string }
interface LicenseAi {
  id: string; companyName: string; keyPrefix: string; enabled: boolean; balance: string; monthUsage: Usage | null
  transactions: Array<{ id: string; type: string; note: string | null; createdAt: string; amount: string }>
}

interface CreditPackage { id: string; name: string; description: string | null; credits: string; priceMinor: number; currency: string }

export function PortalAiCredits(): JSX.Element | null {
  const { t, i18n } = useTranslation('ai')
  const { data } = useQuery({
    queryKey: ['portal-ai'],
    queryFn: () => portalApi.get<{ data: LicenseAi[] }>('/portal/ai').then((r) => r.data.data),
  })
  const queryClient = useQueryClient()
  const [message, setMessage] = useState<MessageModalData | null>(null)
  const { data: store } = useQuery({
    queryKey: ['portal-ai-packages'],
    queryFn: () => portalApi.get<{ data: { available: boolean; packages: CreditPackage[] } }>('/portal/ai/packages').then((r) => r.data.data),
  })
  // Ödeme sayfasından dönüşte (?aiCredit=ok) bilgi ver; bakiye webhook ile gelir, listeyi tazele
  const [returned] = useState(() => new URLSearchParams(window.location.search).get('aiCredit') === 'ok')
  const [returnedShown, setReturnedShown] = useState(returned)
  const checkout = useMutation({
    mutationFn: (v: { licenseId: string; productId: string }) => portalApi.post<{ data: { url: string } }>('/portal/ai/checkout', v).then((r) => r.data.data.url),
    onSuccess: (url) => { window.location.assign(url) },
    onError: () => setMessage({ type: 'error', title: t('portal.errorTitle'), message: t('portal.error') }),
  })
  if (!data || data.length === 0) return null
  const n = (v: number): string => v.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'tr-TR')
  return (
    <Section title={t('portal.title')} description={t('portal.description')}>
      <div className="space-y-4">
        {data.map((l) => (
          <div key={l.id} className="rounded-lg border border-border/50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-foreground">{l.companyName} <span className="font-mono text-xs text-muted-foreground">{l.keyPrefix}…</span></p>
              <span className={`badge ${l.enabled ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>{l.enabled ? t('portal.enabled') : t('portal.disabled')}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div><p className="text-[11px] text-muted-foreground">{t('super.balance')}</p><p className="text-base font-semibold text-foreground">{l.balance}</p></div>
              <div><p className="text-[11px] text-muted-foreground">{t('super.monthCredits')}</p><p className="text-base font-semibold text-foreground">{l.monthUsage?.credits ?? '0.00'}</p></div>
              <div><p className="text-[11px] text-muted-foreground">{t('usage.inputTokens')}</p><p className="text-base text-foreground">{n((l.monthUsage?.inputTokens ?? 0) + (l.monthUsage?.cacheReadTokens ?? 0) + (l.monthUsage?.cacheWriteTokens ?? 0))}</p></div>
              <div><p className="text-[11px] text-muted-foreground">{t('usage.outputTokens')}</p><p className="text-base text-foreground">{n(l.monthUsage?.outputTokens ?? 0)}</p></div>
            </div>
            {store?.available && store.packages.length > 0 && (
              <div className="mt-3 border-t border-border/30 pt-3">
                <p className="text-xs font-medium text-foreground">{t('portal.buyTitle')}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {store.packages.map((p) => (
                    <button key={p.id} type="button" className="btn-secondary text-left" disabled={checkout.isPending}
                      onClick={() => checkout.mutate({ licenseId: l.id, productId: p.id })}>
                      <span className="block text-sm font-semibold">{t('portal.credits', { amount: p.credits })}</span>
                      <span className="block text-[11px] text-muted-foreground">{(p.priceMinor / 100).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'tr-TR', { style: 'currency', currency: p.currency })} · {t('portal.buy')}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">{t('portal.buyHint')}</p>
              </div>
            )}
            {l.transactions.length > 0 && (
              <table className="mt-3 w-full text-xs">
                <tbody className="divide-y divide-border/30">
                  {l.transactions.slice(0, 10).map((x) => (
                    <tr key={x.id}>
                      <td className="py-1.5 text-muted-foreground">{formatDateTime(x.createdAt, i18n.language)}</td>
                      <td className="py-1.5">{t(`super.txTypes.${x.type}`, { defaultValue: x.type })}</td>
                      <td className="py-1.5 text-right font-medium text-foreground">{x.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
        <p className="text-[11px] text-muted-foreground">{t('portal.hint')}</p>
      </div>
      <MessageModal data={message} onClose={() => setMessage(null)} />
      <MessageModal
        data={returnedShown ? { type: 'success', message: t('portal.success') } : null}
        onClose={() => { setReturnedShown(false); void queryClient.invalidateQueries({ queryKey: ['portal-ai'] }) }}
      />
    </Section>
  )
}
